// @flow
import _ from 'lodash';
import keymirror from 'keymirror';

import ApiManager from './ApiManager';

// -- contants
import APIEndpoints from '../constants/APIEndpoints';

// -- entities
import {
  RestaurantMenu,
  Menu,
  ProductOption,
  Product,
  ProductCategory,
  UpsellingCategory,
  MenuPart,
  MenuPartCategory,
} from '../entities/menu';
import ApiError from '../entities/ApiError';

/*
 * Defines the differents values that can be used for the `menuSource` parameter for the
 * get restaurant menu requests.
 */
const StocksSource = keymirror({
  /**
   * to use only the local menu information
   */
  LOCAL_ONLY: null,

  /**
   * to use only the POS menu information (can cause performance issues, depending on the POS)
   */
  POS_ONLY: null,
});

/*
 * Defines the differents values that can be used for the `menuSource` parameter for the
 * get restaurant menu requests.
 */
const MenuSource = keymirror({
  /**
   * to use only the local menu information
   */
  LOCAL_ONLY: null,

  /**
   * to use both local and POS menu information merged (can cause performance issues, depending on
   * the POS).
   */
  LOCAL_AND_POS: null,

  /**
   * to use only the POS menu information (can cause performance issues, depending on the POS)
   */
  POS_ONLY: null,
});

class ApiRestaurantMenu {
  //
  // Menu
  //

  /**
   * Retrieve the restaurant menu
   * This request can take > 10 seconds.
   */
  static getMenu(restaurantId: string, success: Function, failure: Function) {
    const endpoint = APIEndpoints.RESTAURANT_MENU.replace(
      '{restaurantId}',
      restaurantId
    );

    const requestOptions = {
      endpoint,
      query: {
        stocksSource: StocksSource.POS_ONLY,
        menuSource: MenuSource.LOCAL_ONLY,
        loadCartModifiers: true,
      },
      success: (json: Object) => {
        const restaurantMenu: RestaurantMenu = new RestaurantMenu();
        restaurantMenu.fromJson(json);

        success(restaurantMenu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.get(requestOptions);
  }

  static getMenuWithSynchronization(
    restaurantId: string,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU.replace(
      '{restaurantId}',
      restaurantId
    );

    const requestOptions = {
      endpoint,
      query: {
        stocksSource: StocksSource.POS_ONLY, // default true
        menuSource: MenuSource.LOCAL_AND_POS,
      },
      success: (json: Object) => {
        const restaurantMenu: RestaurantMenu = new RestaurantMenu();
        restaurantMenu.fromJson(json);

        success(restaurantMenu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.get(requestOptions);
  }

  /**
   * Retrieve the restaurant menu without synchronize him with the pos
   * The `stocksSource` set to false tell the api to not synchronize, but the stocks will be on the
   * response (without being updated with the POS).
   * @type {[type]}
   */
  static getLocalMenu(
    restaurantId: string,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU.replace(
      '{restaurantId}',
      restaurantId
    );

    const requestOptions = {
      endpoint,
      query: {
        stocksSource: StocksSource.LOCAL_ONLY, // default true
        menuSource: MenuSource.LOCAL_ONLY,
      },
      success: (json: Object) => {
        const restaurantMenu: RestaurantMenu = new RestaurantMenu();
        restaurantMenu.fromJson(json);

        success(restaurantMenu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.get(requestOptions);
  }

  /**
   *
   */
  static putMenu(
    restaurantId: string,
    restaurantMenu: RestaurantMenu,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU.replace(
      '{restaurantId}',
      restaurantId
    );

    const requestOptions = {
      endpoint,
      data: restaurantMenu,
      success: (json: Object) => {
        const restaurantMenu: RestaurantMenu = new RestaurantMenu();
        restaurantMenu.fromJson(json);

        success(restaurantMenu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.put(requestOptions);
  }

  //
  // Menu - Product
  //

  static postMenuProduct(
    restaurantId: string,
    productToCreate: Product,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCTS.replace(
      '{restaurantId}',
      restaurantId
    );

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        product: JSON.stringify(productToCreate),
      },
      files,
      success: (json: Object) => {

        const product: Product = new Product();
        product.fromJson(json);

        success(product);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.postMultipart(requestOptions);
  }

  static putMenuProduct(
    restaurantId: string,
    productToUpdate: Product,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{productId}', productToUpdate.id);

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        product: JSON.stringify(productToUpdate),
      },
      files,
      success: (json: Object) => {

        const product: Product = new Product();
        product.fromJson(json);

        success(product);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.putMultipart(requestOptions);
  }

  static deleteMenuProductImage(
    restaurantId: string,
    productToUpdate: Product,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT_IMAGE.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{productId}', productToUpdate.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {

        const product: Product = new Product();
        product.fromJson(json);

        success(product);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  static deleteMenuProduct(
    restaurantId: string,
    productToDelete: Product,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{productId}', productToDelete.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        success(productToDelete);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  //
  // Menu - Menu
  //

  static postMenuMenu(
    restaurantId: string,
    menuToCreate: Menu,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENUS.replace(
      '{restaurantId}',
      restaurantId
    );

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        menu: JSON.stringify(menuToCreate),
      },
      files,
      success: (json: Object) => {

        const menu: Menu = new Menu();
        menu.fromJson(json);

        success(menu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.postMultipart(requestOptions);
  }

  static putMenuMenu(
    restaurantId: string,
    menuToUpdate: Menu,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{menuId}', menuToUpdate.id);

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        menu: JSON.stringify(menuToUpdate),
      },
      files,
      success: (json: Object) => {

        const menu: Menu = new Menu();
        menu.fromJson(json);

        success(menu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.putMultipart(requestOptions);
  }

  static deleteMenuMenu(
    restaurantId: string,
    menuToDelete: Menu,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{menuId}', menuToDelete.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        success(menuToDelete);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  static deleteMenuMenuImage(
    restaurantId: string,
    menuToUpdate: Menu,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU_IMAGE.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{menuId}', menuToUpdate.id);

    const requestOptions = {
      endpoint,
      headers: { 'restaurant-id': restaurantId },
      success: (json: Object) => {

        const menu: Menu = new Menu();
        menu.fromJson(json);

        success(menu);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  //
  // Menu - Category
  //

  static postMenuCategory(
    restaurantId: string,
    categoryToCreate: ProductCategory,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_CATEGORIES.replace(
      '{restaurantId}',
      restaurantId
    );

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        category: JSON.stringify(categoryToCreate),
      },
      files,
      success: (json: Object) => {

        const category: ProductCategory = new ProductCategory();
        category.fromJson(json);

        success(category);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.postMultipart(requestOptions);
  }

  static putMenuCategory(
    restaurantId: string,
    categoryToUpdate: ProductCategory,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_CATEGORY.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{categoryId}', categoryToUpdate.id);

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        category: JSON.stringify(categoryToUpdate),
      },
      files,
      success,
      failure,
    };

    ApiManager.putMultipart(requestOptions);
  }

  static deleteMenuCategoryImage(
    restaurantId: string,
    categoryToUpdate: ProductCategory,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_CATEGORY_IMAGE.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{categoryId}', categoryToUpdate.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        const category: ProductCategory = new ProductCategory();
        category.fromJson(json);

        success(category);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  static deleteMenuCategory(
    restaurantId: string,
    categoryToDelete: ProductCategory,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_CATEGORY.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{categoryId}', categoryToDelete.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        success(categoryToDelete);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  //
  // Menu - ProductOption
  //

  static postMenuProductOption(
    restaurantId: string,
    productOptionToCreate: ProductOption,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT_OPTIONS.replace(
      '{restaurantId}',
      restaurantId
    );

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        option: JSON.stringify(productOptionToCreate),
      },
      files,
      success: (json: Object) => {

        const productOption: ProductOption = new ProductOption();
        productOption.fromJson(json);

        success(productOption);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.postMultipart(requestOptions);
  }

  static putMenuProductOption(
    restaurantId: string,
    productOptionToUpdate: ProductOption,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT_OPTION.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{productOptionId}', productOptionToUpdate.id);

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        option: JSON.stringify(productOptionToUpdate),
      },
      files,
      success: (json: Object) => {

        const productOption: ProductOption = new ProductOption();
        productOption.fromJson(json);

        success(productOption);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.putMultipart(requestOptions);
  }

  static deleteMenuProductOptionImage(
    restaurantId: string,
    productOptionToUpdate: ProductOption,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT_OPTION_IMAGE.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{productOptionId}', productOptionToUpdate.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {

        const productOption: ProductOption = new ProductOption();
        productOption.fromJson(json);

        success(productOption);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  static deleteMenuProductOption(
    restaurantId: string,
    productOptionToDelete: ProductOption,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_PRODUCT_OPTION.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{productOptionId}', productOptionToDelete.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        success(productOptionToDelete);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  //
  // Menu - Category
  //

  static postMenuUpsellingCategory(
    restaurantId: string,
    upsellingCategoryToCreate: UpsellingCategory,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_UPSELLING_CATEGORIES.replace(
      '{restaurantId}',
      restaurantId
    );

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        upsellingCategory: JSON.stringify(upsellingCategoryToCreate),
      },
      files,
      success: (json: Object) => {

        const upsellingCategory: UpsellingCategory = new UpsellingCategory();
        upsellingCategory.fromJson(json);

        success(upsellingCategory);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.postMultipart(requestOptions);
  }

  static putMenuUpsellingCategory(
    restaurantId: string,
    upsellingCategoryToUpdate: UpsellingCategory,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_UPSELLING_CATEGORY.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{upsellingCategoryId}', upsellingCategoryToUpdate.id);

    const files = {};
    if (!_.isNil(image)) {
      files.image = image;
    }

    const requestOptions = {
      endpoint,
      data: {
        upsellingCategory: JSON.stringify(upsellingCategoryToUpdate),
      },
      files,
      success: (json: Object) => {

        const upsellingCategory: UpsellingCategory = new UpsellingCategory();
        upsellingCategory.fromJson(json);

        success(upsellingCategory);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.putMultipart(requestOptions);
  }

  static deleteMenuUpsellingCategory(
    restaurantId: string,
    upsellingCategoryToDelete: UpsellingCategory,
    image,
    success: Function,
    failure: Function
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_UPSELLING_CATEGORY.replace(
      '{restaurantId}',
      restaurantId
    ).replace('{upsellingCategoryId}', upsellingCategoryToDelete.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        success(upsellingCategoryToDelete);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  //
  // MenuPart
  //

  static updateMenuPart(
    restaurantId,
    menu,
    menuPart,
    success,
    failure
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU_PART.replace(
      '{restaurantId}',
      restaurantId
    )
      .replace('{menuId}', menu.id)
      .replace('{partId}', menuPart.id);

    const requestOptions = {
      endpoint,
      data: menuPart,
      success: (json: Object) => {
        const updatedMenuPart: MenuPart = new MenuPart();
        updatedMenuPart.fromJson(json);

        success(updatedMenuPart);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.put(requestOptions);
  }

  //
  // MenuPartCategory
  //

  static updateMenuPartCategory(
    restaurantId,
    menu,
    menuPart,
    menuPartCategory,
    success,
    failure
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU_PART_CATEGORY.replace(
      '{restaurantId}',
      restaurantId
    )
      .replace('{menuId}', menu.id)
      .replace('{partId}', menuPart.id)
      .replace('{categoryId}', menuPartCategory.id);

    const requestOptions = {
      endpoint,
      data: menuPartCategory,
      success: (json: Object) => {

        const updatedMenuPartCategory: MenuPartCategory = new MenuPartCategory();
        updatedMenuPartCategory.fromJson(json);


        success(updatedMenuPartCategory);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.put(requestOptions);
  }

  static uploadMenuPartCategoryImage(
    restaurantId,
    menu,
    menuPart,
    menuPartCategory,
    image,
    success,
    failure
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU_PART_CATEGORY_IMAGE.replace(
      '{restaurantId}',
      restaurantId
    )
      .replace('{menuId}', menu.id)
      .replace('{partId}', menuPart.id)
      .replace('{categoryId}', menuPartCategory.id);

    const files = {};
    files.image = image;

    const requestOptions = {
      endpoint,
      files,
      success: (json: Object) => {
        const updatedMenuPartCategory: MenuPartCategory = new MenuPartCategory();
        updatedMenuPartCategory.fromJson(json);

        success(updatedMenuPartCategory);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.postMultipart(requestOptions);
  }

  static removeMenuPartCategoryImage(
    restaurantId,
    menu,
    menuPart,
    menuPartCategory,
    success,
    failure
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU_PART_CATEGORY_IMAGE.replace(
      '{restaurantId}',
      restaurantId
    )
      .replace('{menuId}', menu.id)
      .replace('{partId}', menuPart.id)
      .replace('{categoryId}', menuPartCategory.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {

        const updatedMenuPartCategory: MenuPartCategory = new MenuPartCategory();
        updatedMenuPartCategory.fromJson(json);

        success(updatedMenuPartCategory);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }

  static deleteMenuPartCategory(
    restaurantId,
    menu,
    menuPart,
    menuPartCategory,
    success,
    failure
  ) {
    const endpoint = APIEndpoints.RESTAURANT_MENU_MENU_PART_CATEGORY.replace(
      '{restaurantId}',
      restaurantId
    )
      .replace('{menuId}', menu.id)
      .replace('{partId}', menuPart.id)
      .replace('{categoryId}', menuPartCategory.id);

    const requestOptions = {
      endpoint,
      success: (json: Object) => {
        success();
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      },
    };

    ApiManager.delete(requestOptions);
  }
}

export default ApiRestaurantMenu;
